import { useId, useState } from "react";
import { ProjectId } from "../data/projects";
import { systemStories } from "../data/systemStories";
import {
  decimal,
  DemoSet,
  EntryKind,
  ledgerPreview,
  money,
  portionPreview,
  sessionVolume,
} from "../lib/demoModels";

function Stepper({
  label,
  value,
  min,
  max,
  step = 1,
  unit = "",
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="stepper">
      <span className="micro">{label}</span>
      <div>
        <button
          aria-label={`Decrease ${label.toLowerCase()}`}
          disabled={value <= min}
          onClick={() => onChange(Math.max(min, value - step))}
        >
          −
        </button>
        <output aria-label={label}>
          {value}
          <small>{unit}</small>
        </output>
        <button
          aria-label={`Increase ${label.toLowerCase()}`}
          disabled={value >= max}
          onClick={() => onChange(Math.min(max, value + step))}
        >
          +
        </button>
      </div>
    </div>
  );
}

export function ProductLab({
  projectId,
  context = "project",
}: {
  projectId: ProjectId;
  context?: "hero" | "project";
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(250);
  const [kind, setKind] = useState<EntryKind>("transfer");
  const [grams, setGrams] = useState(150);
  const [weight, setWeight] = useState(40);
  const [reps, setReps] = useState(8);
  const [sets, setSets] = useState<DemoSet[]>([]);
  const [resting, setResting] = useState(false);
  const [notice, setNotice] = useState("No sets recorded yet.");
  const ledger = ledgerPreview(amount, kind);
  const nutrition = portionPreview(grams);
  const story = systemStories[projectId];
  const name = projectId[0].toUpperCase() + projectId.slice(1);
  const result =
    projectId === "tracker"
      ? `${money(ledger.spending)} spending`
      : projectId === "counter"
      ? `${decimal(nutrition.calories)} kcal`
      : `${sets.length} ${sets.length === 1 ? "set" : "sets"} recorded`;
  const signals =
    projectId === "tracker"
      ? [
          `${money(amount)} · ${kind}`,
          kind === "transfer"
            ? "Move value. Preserve total."
            : "Deduct value. Count spending.",
          `${money(ledger.total)} across accounts`,
        ]
      : projectId === "counter"
      ? [
          `${grams} g selected`,
          `${grams} ÷ 100 = ${nutrition.ratio.toString()}×`,
          `${decimal(nutrition.calories)} kcal preview`,
        ]
      : [
          `Draft: ${weight} kg × ${reps}`,
          `${sets.length} saved ${sets.length === 1 ? "set" : "sets"}`,
          `${sessionVolume(sets)} kg recorded volume`,
        ];
  function reset() {
    setAmount(250);
    setKind("transfer");
    setGrams(150);
    setWeight(40);
    setReps(8);
    setSets([]);
    setResting(false);
    setNotice("Demo reset. No sets recorded.");
  }
  function undo() {
    const last = sets[sets.length - 1];
    if (!last) return;
    setSets(sets.slice(0, -1));
    setWeight(last.weight);
    setReps(last.reps);
    setResting(false);
    setNotice("Last set removed. Draft restored.");
  }
  return (
    <div
      className={`product-lab ${projectId} ${open ? "layers-open" : ""}`}
      role="region"
      aria-label={`${name} ${context === "hero" ? "hero" : "product"} demo`}
    >
      <div className="lab-toolbar">
        <span className="micro">
          <span className="signal-dot" /> {name} / working model
        </span>
        <button
          className="reset-button"
          onClick={reset}
          aria-label={`Reset ${name} demo`}
        >
          Reset ↺
        </button>
      </div>
      <div className="lab-body">
        <div className="lab-interface">
          <div className="interface-label">
            <span>INTERFACE</span>
            <span aria-hidden="true">01</span>
          </div>
          {projectId === "tracker" && (
            <>
              <div className="model-title">
                <span>Between your accounts.</span>
                <strong>
                  {money(amount)}
                  <small>USD</small>
                </strong>
              </div>
              <div
                className="segmented"
                role="group"
                aria-label="Transaction type"
              >
                {(["transfer", "expense"] as EntryKind[]).map((type) => (
                  <button
                    key={type}
                    aria-pressed={kind === type}
                    onClick={() => setKind(type)}
                  >
                    {type === "transfer" ? "Transfer" : "Expense"}
                  </button>
                ))}
              </div>
              <label className="range-label" htmlFor={`${id}-amount`}>
                Amount <span>{money(amount)}</span>
              </label>
              <input
                id={`${id}-amount`}
                aria-label="Transaction amount"
                type="range"
                min="50"
                max="500"
                step="50"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
              />
              <div className="account-pair">
                <div>
                  <span>Checking</span>
                  <strong>{money(ledger.checking)}</strong>
                </div>
                <span className="account-arrow" aria-hidden="true">
                  {kind === "transfer" ? "→" : "↗"}
                </span>
                <div>
                  <span>Savings</span>
                  <strong>{money(ledger.savings)}</strong>
                </div>
              </div>
              <div className="model-outcome" aria-live="polite">
                <span>Spending impact</span>
                <strong>{money(ledger.spending)}</strong>
              </div>
            </>
          )}
          {projectId === "counter" && (
            <>
              <div className="model-title">
                <span>Your portion, in perspective.</span>
                <strong>
                  {decimal(nutrition.calories)}
                  <small>kcal</small>
                </strong>
              </div>
              <label className="range-label" htmlFor={`${id}-portion`}>
                Sample food portion <span>{grams} g</span>
              </label>
              <input
                id={`${id}-portion`}
                aria-label="Food portion in grams"
                type="range"
                min="25"
                max="300"
                step="25"
                value={grams}
                onChange={(e) => setGrams(Number(e.target.value))}
              />
              <div className="portion-ruler" aria-hidden="true">
                <span>25 g</span>
                <span>150 g</span>
                <span>300 g</span>
              </div>
              <div className="macro-readings" aria-live="polite">
                {[
                  ["Protein", nutrition.protein],
                  ["Carbs", nutrition.carbs],
                  ["Fat", nutrition.fat],
                ].map(([label, value]) => (
                  <div key={label}>
                    <span>{label}</span>
                    <strong>
                      {decimal(Number(value))}
                      <small>g</small>
                    </strong>
                  </div>
                ))}
              </div>
              <div className="model-outcome">
                <span>Reference amount</span>
                <strong>
                  100 g <small>/ 198 kcal</small>
                </strong>
              </div>
            </>
          )}
          {projectId === "spotter" && (
            <>
              <div className="model-title">
                <span>Sample strength session.</span>
                <strong>
                  {String(sets.length + 1).padStart(2, "0")}
                  <small>next set</small>
                </strong>
              </div>
              <div className="set-controls">
                <Stepper
                  label="Weight"
                  value={weight}
                  min={0}
                  max={100}
                  step={2.5}
                  unit="kg"
                  onChange={setWeight}
                />
                <Stepper
                  label="Reps"
                  value={reps}
                  min={1}
                  max={20}
                  onChange={setReps}
                />
              </div>
              <div className="set-actions">
                <button
                  className="demo-primary"
                  disabled={sets.length >= 8 && !resting}
                  onClick={() => {
                    if (resting) {
                      setResting(false);
                      setNotice("Ready for the next set.");
                    } else {
                      setSets([...sets, { weight, reps }]);
                      setResting(true);
                      setNotice(
                        `Set ${
                          sets.length + 1
                        } recorded: ${weight} kilograms, ${reps} reps.`
                      );
                    }
                  }}
                >
                  {resting
                    ? "End rest →"
                    : sets.length >= 8
                    ? "Demo complete"
                    : "Log set +"}
                </button>
                <button
                  className="undo-button"
                  disabled={!sets.length}
                  onClick={undo}
                >
                  Undo
                </button>
              </div>
              <div className="set-history" aria-label="Recorded sets">
                {Array.from({ length: 8 }, (_, i) => (
                  <span
                    key={i}
                    className={i < sets.length ? "is-recorded" : ""}
                    aria-hidden="true"
                  />
                ))}
                <span>{sets.length}/8 sets</span>
              </div>
              <div className="model-outcome">
                <span>Recorded volume</span>
                <strong>
                  {sessionVolume(sets)}
                  <small>kg</small>
                </strong>
              </div>
              <p className="sr-only" role="status">
                {notice}
              </p>
            </>
          )}
        </div>
        <div className="layer-stack" id={`${id}-layers`} hidden={!open}>
          <div className="interface-label">
            <span>UNDER THE INTERFACE</span>
            <span aria-hidden="true">02—04</span>
          </div>
          <ol className="trace-steps">
            {story.layers.map((layer, i) => (
              <li key={layer.name}>
                <span className="trace-node" aria-hidden="true">
                  0{i + 2}
                </span>
                <div>
                  <p className="micro">{layer.name}</p>
                  <strong>{signals[i]}</strong>
                  <p>{layer.description}</p>
                  <code>{layer.source}</code>
                </div>
              </li>
            ))}
          </ol>
          <p className="trace-note">
            App architecture, simplified. The web model demonstrates the rule;
            it does not run native storage or device services.
          </p>
        </div>
      </div>
      <button
        className="layer-toggle"
        aria-expanded={open}
        aria-controls={`${id}-layers`}
        onClick={() => setOpen(!open)}
      >
        <span className="layer-icon" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <span>{open ? "Close the layers" : "Reveal the system"}</span>
        <span aria-hidden="true">{open ? "−" : "+"}</span>
      </button>
      <div className="lab-caption">
        <span>Interactive study · sample data · resets on reload</span>
        <output aria-label={`${name} demo result`}>{result}</output>
      </div>
    </div>
  );
}
